import { v4 as uuidv4 } from 'uuid';
import {
  FLYER_GENERATION_ENDPOINT,
  AWS_BUCKET_MMS,
  S3_GET_PRESIGNED_URLS
} from '../../../utils/constants';

/**
 * Generates a flyer image by calling your Lambda function
 * The Lambda uses GoogleGenAI SDK to generate the image and uploads to S3
 * @param {string} prompt - The prompt for image generation
 * @param {string} clientId - Client ID for S3 organization
 * @param {string|null} referenceImageUrl - Optional client image URL to use inside the flyer
 * @returns {Promise<string>} S3 URL of the generated image
 */
export async function generateFlyerWithGemini(
  prompt,
  clientId = 'flyers',
  referenceImageUrl = null
) {
  try {
    if (!FLYER_GENERATION_ENDPOINT) {
      throw new Error(
        'FLYER_GENERATION_ENDPOINT is not configured in environment variables'
      );
    }

    const response = await fetch(FLYER_GENERATION_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        clientId: clientId,
        imagePrompt: prompt,
        referenceImageUrl: referenceImageUrl || null
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        `Image generation error: ${
          errorData.error || response.statusText || 'Unknown error'
        }`
      );
    }

    const data = await response.json();

    console.log('Lambda response:', data);

    if (!data.success) {
      throw new Error(data.error || 'Image generation failed');
    }

    // Your Lambda already returns the S3 URL
    console.log('S3 URL from Lambda:', data.s3Url);
    return data.s3Url;
  } catch (error) {
    console.error('Error generating flyer:', error);
    throw error;
  }
}

/**
 * Converts base64 image to File object
 * @param {string} base64Data - Base64 encoded image data
 * @param {string} fileName - Name for the file
 * @returns {File} File object
 */
export function base64ToFile(base64Data, fileName = `flyer-${uuidv4()}.png`) {
  // Remove data URL prefix if present
  const base64Clean = base64Data.replace(/^data:image\/\w+;base64,/, '');

  // Convert base64 to binary
  const binaryString = atob(base64Clean);
  const bytes = new Uint8Array(binaryString.length);

  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  // Create blob and then file
  const blob = new Blob([bytes], { type: 'image/png' });
  return new File([blob], fileName, { type: 'image/png' });
}

/**
 * Uploads a file to S3 using presigned URL (same process as ImageUploader)
 * @param {File} file - File to upload
 * @returns {Promise<string>} S3 URL of uploaded file
 */
export async function uploadFlyerToS3(file) {
  const fileType = file.type;
  const fileName = file.name;

  try {
    // 1. Get presigned URL from API Gateway
    const presignedResponse = await fetch(S3_GET_PRESIGNED_URLS, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        bucketName: AWS_BUCKET_MMS,
        files: [{ fileName, fileType }]
      })
    });

    if (!presignedResponse.ok) {
      throw new Error('Failed to get presigned URL');
    }

    const { urls } = await presignedResponse.json();
    const presignedUrl = urls?.[0]?.url;

    if (!presignedUrl) {
      throw new Error('No presigned URL returned');
    }

    // 2. Upload file directly to S3
    const uploadResult = await fetch(presignedUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': fileType
      },
      body: file
    });

    if (!uploadResult.ok) {
      throw new Error('File upload to S3 failed');
    }

    // 3. Return the S3 URL
    const s3Url = `https://${AWS_BUCKET_MMS}.s3.amazonaws.com/${fileName}`;
    return s3Url;
  } catch (error) {
    console.error('Error uploading flyer to S3:', error);
    throw error;
  }
}

/**
 * Complete workflow: Generate flyer and get S3 URL
 * Your Lambda handles both generation and S3 upload
 * @param {string} prompt - The prompt for flyer generation
 * @param {string} clientId - Client ID for S3 organization
 * @param {string|null} referenceImageUrl - Optional client image URL to use inside the flyer
 * @returns {Promise<{url: string}>} S3 URL
 */
export async function generateAndUploadFlyer(
  prompt,
  clientId,
  referenceImageUrl = null // 👈 CAMBIO: tercer parámetro opcional
) {
  try {
    // Lambda does everything: generate with GoogleGenAI + upload to S3
    const s3Url = await generateFlyerWithGemini(
      prompt,
      clientId,
      referenceImageUrl // 👈 CAMBIO: se pasa hacia abajo
    );

    console.log('generateAndUploadFlyer returning URL:', s3Url);

    return {
      url: s3Url
    };
  } catch (error) {
    console.error('Error in generate and upload workflow:', error);
    throw error;
  }
}
